import * as angular from 'angular';
import * as _ from 'lodash';
import { Mutex } from 'async-mutex';

import { ISessionStorageService, IWorkspaceService, ILoggerService, IAuthenticationService } from './';
import { authorizationDataKey } from '../../common/constants';
import { StoredToken } from './StoredToken';
import isExpired from './helpers/isExpired';

const mutex = new Mutex();

interface Rejection {
    config?: angular.IRequestConfig;
    data?: any;
    message?: string;
    status?: number;
}

export interface IAuthenticationInterceptorService {
    request(config);
    responseError(rejection);
}

export class AuthenticationInterceptorService implements IAuthenticationInterceptorService, angular.IHttpInterceptor {
    static serviceId = 'authenticationInterceptorService';
    private static self: AuthenticationInterceptorService;

    static $inject = [
        '$q',
        '$injector',
        'sessionStorageService',
        'workspaceService',
        'loggerService'
    ];
    constructor(private $q: angular.IQService,
        private $injector,
        private sessionStorageService: ISessionStorageService,
        private workspaceService: IWorkspaceService,
        private loggerService: ILoggerService) {
        // keep reference to object instance
        AuthenticationInterceptorService.self = this;
    }

    request(config: angular.IRequestConfig) {
        const { self } = AuthenticationInterceptorService;
        config.headers = config.headers || {};
        if (!_.endsWith(config.url, 'token') && !_.endsWith(config.url, 'config.json')) {
            return mutex.waitForUnlock()
                .then(() => {
                    return mutex.acquire()
                        .then(release => {
                            const authData: StoredToken = JSON.parse(self.sessionStorageService.getFrom(authorizationDataKey));
                            if (authData?.expirationDate && isExpired(authData)) {
                                return self.refreshToken(self)
                                    .then(data => {
                                        self.setHeaders(config, self);
                                        return config;
                                    })
                                    .finally(() => release());
                            } else {
                                self.setHeaders(config, self);
                                release();
                                return config;
                            }
                        });
                });
        } else {
            return config;
        }
    }

    private setHeaders(config: angular.IRequestConfig, self: AuthenticationInterceptorService) {
        try {
            const authData = JSON.parse(self.sessionStorageService.getFrom(authorizationDataKey));
            if (authData?.token) {
                config.headers.Authorization = `Bearer ${authData.token}`;
            }
            const workspace = self.workspaceService.getWorkspace();
            if (workspace) {
                workspace.application = encodeURIComponent(workspace.application);
                config.headers['Wt-Workspace'] = JSON.stringify(workspace);
            }
        } catch (e) {
            self.loggerService.logError(e);
        }
    }

    private refreshToken(self: AuthenticationInterceptorService) {
        return (self.$injector.get('authenticationService') as IAuthenticationService)
            .refreshToken()
            .catch(data => {
                console.error(data);
                return self.expireRedirect(data);
            });
    }

    response<T>(response: angular.IHttpResponse<T>): angular.IHttpResponse<T> | angular.IPromise<angular.IHttpResponse<T>> {
        return response;
    }

    responseError(rejection: Rejection) {
        const { self } = AuthenticationInterceptorService;
        const authData: StoredToken = JSON.parse(self.sessionStorageService.getFrom(authorizationDataKey));
        if (rejection.status === 0) {
            self.loggerService.logError('We aren\'t able to contact your WiDE server.');
        } else if (rejection.status === 401 && isExpired(authData)) {
            return self.refreshToken(self)
                .then(data => {
                    return (self.$injector.get('$http'))(rejection.config);
                });
        } else {
            if (rejection?.data?.error === 'invalid_grant') {
                self.loggerService.logError('Failed to fetch token', false);
            } else {
                const message = rejection.message
                    ? rejection.message
                    : rejection.data?.Message ?? 'N/A';
                const errorMessage = `Error code ${rejection.status}: ${message}`;
                if (rejection.status === 400 || rejection.status === 401) {
                    self.loggerService.logError(errorMessage, false);
                }
            }
        }

        return self.$q.reject(rejection);
    }

    private expireRedirect(config) {
        const { self } = AuthenticationInterceptorService;
        const stateService: angular.ui.IStateService = self.$injector.get('$state');
        if (stateService.current.name !== 'login') {
            self.loggerService.logWarning('Your session has expired.');
            self.$injector.get('authenticationService').logout();
            stateService.go('login');
        }
        return self.$q.reject(config);
    }
}